import React, {useState, useRef, useId, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Flex, Box, Text, useStyleConfig} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import PromoSlideVideoCard from './partials/promo-slide-video-card'
import {remCalc} from '../../../theme/_utils'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import PromoSliderIcons from './promo-slider-icon-arrow'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

export const PromoSliderVideo = ({
    sliderGroup,
    title,
    abTitle = '',
    isVariantStyle = false,
    contentType = '',
    currentVideoSlide = noop,
}) => {
    const uniqueId = useId().replace(/:/g, '')
    const swiperStyles = useStyleConfig('Swiper', {
        variant: isVariantStyle && 'paperlessVariant',
    })
    const styles = useStyleConfig('PromoSlider', {
        variant: isVariantStyle && 'paperlessVariant',
    })
    const [isPlaying, setIsPlaying] = useState(false)
    const swiperRef = useRef(null)
    const prevButtonRef = useRef(null)
    const nextButtonRef = useRef(null)
    const navigationDirection = useRef(null)
    const [activeSlideIndex, setActiveSlideIndex] = useState(0)
    //create a custom buffer for the slides
    const extendedSliderGroup = [...(sliderGroup || []), ...(sliderGroup || [])]

    const slidesToView = {
        base: 1.2,
        sm: 1.3,
        md: 1.6,
        lg: 1.8,
        xl: 2,
        xxl: 2.5,
    }

    useEffect(() => {
        function updateBulletTabIndex() {
            const isMobile = window.innerWidth < 1024 // adjust breakpoint
            document.querySelectorAll('.promo-slider-pagination button').forEach((btn) => {
                btn.tabIndex = isMobile ? 0 : -1
            })
        }

        updateBulletTabIndex()
        window.addEventListener('resize', updateBulletTabIndex)

        return () => {
            window.removeEventListener('resize', updateBulletTabIndex)
        }
    }, [])

    useEffect(() => {
        if (extendedSliderGroup[activeSlideIndex]) {
            currentVideoSlide({
                title: extendedSliderGroup[activeSlideIndex]?.text_block?.title || '',
                source: extendedSliderGroup[activeSlideIndex]?.video_link?.href || '',
                thumbnail: extendedSliderGroup[activeSlideIndex]?.thumbnail?.src || '',
                slideIndex: activeSlideIndex,
                isPlaying: isPlaying,
            })
        }
    }, [activeSlideIndex, isPlaying])

    if (extendedSliderGroup.length === 0) return null

    return (
        <Box data-ab={abTitle}>
            <Text as="h2" {...styles.videoTitle}>
                {title}
            </Text>
            <Box
                __css={swiperStyles}
                py={{base: remCalc(16), md: remCalc(40)}}
                data-testid="promo-slider-video-container"
            >
                <Box position="relative">
                    <HideOnMobile>
                        {/* note chevronIcon is used all swipers thats why top is being overwritten*/}
                        <PromoSliderIcons.left
                            ref={prevButtonRef}
                            className={`swiper-button-prev swiper-button-prev-${uniqueId}`}
                            aria-label="swiper button prev icon"
                            tabIndex={0}
                            onClick={() => {
                                navigationDirection.current = 'prev'
                                swiperRef.current?.slidePrev()
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    navigationDirection.current = 'prev'
                                    swiperRef.current?.slidePrev()
                                }
                            }}
                            position="absolute"
                            zIndex={10}
                            sx={{...styles.chevronIcon, top: {md: 'calc(50% - 60px)', lg: '50%'}}}
                            left={{base: '4%', md: '18%', lg: '22%', xl: '26%'}}
                        />
                    </HideOnMobile>
                    <Swiper
                        modules={[Navigation, Pagination, Keyboard]}
                        navigation={{
                            prevEl: `.swiper-button-prev-${uniqueId}`,
                            nextEl: `.swiper-button-next-${uniqueId}`,
                        }}
                        pagination={{
                            clickable: true,
                            el: '.promo-slider-pagination',
                            renderBullet: (index, className) => {
                                // Render bullets only for original slides length
                                if (index >= sliderGroup.length) return ''
                                return `<button class="${className}" aria-label="slide ${
                                    index + 1
                                }" tabindex="-1"></button>`
                            },
                        }}
                        keyboard={{
                            enabled: true,
                            onlyInViewport: false,
                            pageUpDown: false,
                        }}
                        loop={true}
                        centeredSlides={true}
                        tabIndex={-1}
                        slidesPerView="auto"
                        spaceBetween={16}
                        onInit={(swiper) => {
                            swiperRef.current = swiper
                            const bullets = document.querySelectorAll(
                                '.promo-slider-video-container .swiper-pagination-bullet',
                            )
                            bullets.forEach((bullet) => {
                                bullet.setAttribute('tabindex', '-1')
                            })

                            swiper.slides.forEach((slide, idx) => {
                                const isActive = idx === swiper.activeIndex
                                const wrapper = slide.querySelector(
                                    '[data-testid="promo-slide-video-container"]',
                                )
                                if (wrapper) {
                                    wrapper.setAttribute('tabindex', isActive ? '0' : '-1')
                                    const allDescendants = wrapper.querySelectorAll('*')
                                    allDescendants.forEach((el) => {
                                        el.setAttribute('tabindex', '-1')
                                    })
                                }
                            })
                        }}
                        onSlideChange={(swiper) => {
                            //since we have duplicate slides we need to keep track of the index that corespondes to the real position
                            const originalIndex = swiper.realIndex % sliderGroup.length
                            document
                                .querySelectorAll('.promo-slider-pagination button')
                                .forEach((bullet, idx) => {
                                    bullet.classList.toggle(
                                        'swiper-pagination-bullet-active',
                                        idx === originalIndex,
                                    )
                                })

                            swiper.slides.forEach((slide, idx) => {
                                const isActive = idx === swiper.activeIndex
                                const wrapper = slide.querySelector(
                                    '[data-testid="promo-slide-video-container"]',
                                )
                                if (wrapper) {
                                    wrapper.setAttribute('tabindex', isActive ? '0' : '-1')
                                    const allDescendants = wrapper.querySelectorAll('*')
                                    allDescendants.forEach((el) => {
                                        el.setAttribute('tabindex', '-1')
                                    })
                                }
                            })

                            if (navigationDirection.current) {
                                setTimeout(() => {
                                    const targetSlide = swiper.slides[swiper.activeIndex]
                                    if (targetSlide) {
                                        const wrapper = targetSlide.querySelector(
                                            '[data-testid="promo-slide-video-container"]',
                                        )
                                        if (wrapper) {
                                            wrapper.focus()
                                        }
                                    }
                                    navigationDirection.current = null
                                }, 150)
                            }
                        }}
                        onTransitionEnd={(swiper) => setActiveSlideIndex(swiper.activeIndex)}
                        breakpoints={{
                            0: {
                                spaceBetween: 8,
                                slidesPerView: isVariantStyle ? 1 : 'auto',
                            },
                            768: {
                                spaceBetween: 16,
                                slidesPerView: 'auto',
                            },
                            1024: {
                                slidesPerView: slidesToView.lg,
                                spaceBetween: 75,
                            },
                            1200: {
                                slidesPerView: slidesToView.xl,
                                spaceBetween: 100,
                            },
                            1400: {
                                slidesPerView: slidesToView.xxl,
                                spaceBetween: 143,
                            },
                        }}
                        className="promo-slider-swiper"
                    >
                        {extendedSliderGroup.map((slider, index) => (
                            <SwiperSlide key={index}>
                                <PromoSlideVideoCard
                                    label={slider?.text_block?.label}
                                    title={slider?.text_block?.title}
                                    source={slider?.video_link}
                                    thumbnail={slider?.thumbnail}
                                    isVariantStyle={isVariantStyle}
                                    isPlaying={isPlaying}
                                    setIsPlaying={setIsPlaying}
                                    tabIndex={-1}
                                    contentType={contentType}
                                />
                                {!isPlaying && (
                                    <HideOnDesktop>
                                        <Flex
                                            {...styles.content}
                                            data-testid="promo-slide-video-mobile-content"
                                        >
                                            <Text {...styles.label}>
                                                {slider?.text_block?.label}
                                            </Text>
                                            <Text {...styles.title}>
                                                {slider?.text_block?.title}
                                            </Text>
                                        </Flex>
                                    </HideOnDesktop>
                                )}
                            </SwiperSlide>
                        ))}

                        <Box className="promo-slider-pagination" />
                    </Swiper>
                    <HideOnMobile>
                        <PromoSliderIcons.right
                            ref={nextButtonRef}
                            className={`swiper-button-next swiper-button-next-${uniqueId}`}
                            aria-label="swiper button next icon"
                            tabIndex={0}
                            onClick={() => {
                                navigationDirection.current = 'next'
                                swiperRef.current?.slideNext()
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    navigationDirection.current = 'next'
                                    swiperRef.current?.slideNext()
                                }
                            }}
                            position="absolute"
                            zIndex={10}
                            sx={{...styles.chevronIcon, top: {md: 'calc(50% - 60px)', lg: '50%'}}}
                            right={{base: '4%', md: '18%', lg: '22%', xl: '26%'}}
                        />
                    </HideOnMobile>
                </Box>
            </Box>
        </Box>
    )
}

PromoSliderVideo.propTypes = {
    sliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            text_block: PropTypes.shape({
                label: PropTypes.string,
                title: PropTypes.string,
            }),
            video_link: PropTypes.shape({
                href: PropTypes.string,
                title: PropTypes.string,
            }),
        }),
    ),
    title: PropTypes.string,
    abTitle: PropTypes.string,
    isVariantStyle: PropTypes.bool,
    contentType: PropTypes.string,
    currentVideoSlide: PropTypes.func,
}
